// Per-Block image-target registry. Per the Firebase Storage sub-project
// plan §6 (P-FS-04). Keys are dotted `{blockId}.{field-path}` strings
// matching the form-generator's field paths; array indices are stripped
// before lookup so `event-card-group.events.0.imageUrl` resolves to the
// `event-card-group.events.imageUrl` entry. Targets are `@1x` rendered
// dimensions; the resize step uploads at 2× for retina HiDPI (D-121).
//
// 5 entries Banks-confirmed at P-037.22 / D-130; P-037.24 / D-132 adds 3
// big3-signature-events entries (event image 190 × 100, Big 3 logo 100 ×
// 100). The catch-all `DEFAULT_TARGET` (600 × 600) covers any future
// image field that hasn't been pinned yet.

export interface ImageTarget {
  readonly width: number
  readonly height: number
  /** P-037.29 post-Phase-Close §item G (supersedes §item C's
   *  short-lived `'outside'` mode). Resize-fit mode for the upload
   *  pipeline. Default `'inside'` (fit-inside, preserve aspect inside
   *  the box; D-121 default; used by `author.authorSignatureUrl`
   *  to keep the WHOLE transparent signature visible without
   *  cropping). `'cover'` cover-crops the source at upload time:
   *  center-crops to the target aspect, scales to exact target dims
   *  (clamped to never-upscale). Used by IMG-render consumers + the
   *  two composite-source consumers — bakes the right aspect into
   *  the bytes so the browser's IMG-tag scale (which behaves like
   *  CSS `object-fit: fill`) doesn't distort. The composite Canvas's
   *  internal `coverSourceRect` becomes a no-op for these pre-cropped
   *  sources — same final composite, smaller upload bytes. */
  readonly mode?: 'inside' | 'cover'
  /** P-037.29 post-Phase-Close §item G. When `true`, the resize step
   *  bakes a soft inset shadow into the image bytes via the shared
   *  `applyInsetShadow` from `src/lib/image-shadow.ts`. Used by
   *  direct-render IMG fields (EventCard, SponsorCard, `images`
   *  Block) so the visual effect renders in every email client —
   *  Outlook desktop drops CSS `box-shadow: inset` silently.
   *  Composite-source fields (`big3Events.imageUrl`,
   *  `big-3-hero-image.imageUrl`) do NOT set this — the composite
   *  pipeline's internal `applyInsetShadow` call on the event-image
   *  region bakes the shadow into the final composite PNG; setting
   *  this flag too would double-apply. */
  readonly bakeInsetShadow?: boolean
  /** P-037.30 seventh post-Phase-Close §C. Human-readable label for
   *  the image-type — drives BOTH the form-field label (`renderImage-
   *  Url` overrides the humanized FieldNode label) AND the library
   *  modal title (`<label> Library`). Required for the 11 known
   *  fields; absent on the `DEFAULT_TARGET` fallback (the modal +
   *  field-renderer fall back to the humanized field name there).
   *  Banks-confirmed at planning over 4 alternative title formats
   *  ("<label> Images" / "<label>" / etc.). Labels keep the modal-
   *  scope-isolated UX honest: identical labels on two different
   *  fields STILL share no Storage folder — the folder is keyed on
   *  the dotted path, not on the label. */
  readonly label?: string
}

const RETINA_SCALE = 2

const IMAGE_TARGETS: Readonly<Record<string, ImageTarget>> = Object.freeze({
  // author headshot — 100 × 100 round in AuthorByline (P-037.22 D-130;
  // was 24 × 24 pre-D-130). Post-Phase-Close §item G — `mode: 'cover'`
  // so non-square sources center-crop at upload (avatar's circular CSS
  // clip-mask shows the cropped square). **Block-id rename at D-136:
  // `commentary` was split into 4 atomic Blocks; the headshot field
  // moved to the new `author` Block.** This key must match the
  // post-split dotted path (`author.authorPhotoUrl`); the stale
  // `commentary.authorPhotoUrl` key from pre-D-136 was a latent bug
  // that surfaced at P-037.31 second post-Phase-Close (Banks's
  // preview-walk on the Author Photo field showed "Minimum size:
  // 1200 × 1200 px" — the DEFAULT_TARGET fallback — because the
  // pre-rename key didn't resolve). Fix renames here keep the registry
  // in lockstep with the field path emitted by the form generator.
  'author.authorPhotoUrl': { width: 100, height: 100, mode: 'cover', label: 'Author Photo' },
  // author signature — 200 × 60 transparent PNG. Stays at the
  // `'inside'` default (post-Phase-Close §item G) — we want the WHOLE
  // signature visible without cropping; staff-supplied signatures
  // already crop to a tight bbox. Browser may stretch slightly if
  // aspect ≠ 200:60, acceptable trade-off over losing the signature
  // tail to a cover-crop. **Block-id rename at D-136:** same
  // commentary→author rename as the photo field above.
  'author.authorSignatureUrl': { width: 200, height: 60, label: 'Author Signature' },
  // event-card-group per-event image — 190 × 100 fixed (P-037.22 D-130;
  // was 280 × 210 placeholder). Layout pinned at 3-col 190 px cells with
  // image edge-to-edge across each cell; the 1.9:1 aspect ratio is the
  // contract source images crop to. Post-Phase-Close §item G — `mode:
  // 'cover'` (was fit-inside default — non-1.9:1 sources stretched at
  // the IMG tag, distorting aspect) + `bakeInsetShadow: true` so the
  // inset-shadow ring renders in every client (Outlook drops CSS
  // box-shadow: inset).
  'event-card-group.events.imageUrl': {
    width: 190,
    height: 100,
    mode: 'cover',
    bakeInsetShadow: true,
    label: 'Event Image',
  },
  // day-chairs member headshot — 36 × 36 round in DayChairItem
  // (P-037.25 D-133; was 100 × 100 between D-130 and D-133; was 40 × 40
  // pre-D-130). Banks reverted the D-130 stacked layout back to
  // side-by-side at D-133 — the avatar sits in a 36 px column to the
  // left of the name/event content stack, and the no-photo arm
  // preserves the same column so names align across entries. Post-
  // Phase-Close §item G — `mode: 'cover'` (target is square; cover-
  // crop centers non-square sources for the CSS circular clip-mask).
  'day-chairs.entries.memberPhotoUrl': {
    width: 36,
    height: 36,
    mode: 'cover',
    label: 'Day Chair Photo',
  },
  // chapter-sponsors logo — 190 × 100 (P-037.22 D-130; was 200 × 100
  // pre-D-130). Logo width lines up with the system-wide 190 px 3-col
  // cell convention from D-117; height unchanged. Post-Phase-Close
  // §item G — `mode: 'cover'` (Banks-confirmed at planning despite
  // the brand-text-crop risk on wide-aspect logos; staff expected to
  // pre-crop sponsor logos to 1.9:1 source) + `bakeInsetShadow: true`
  // for cell-shadow parity with the other 3-col card consumers.
  'chapter-sponsors.sponsors.logoUrl': {
    width: 190,
    height: 100,
    mode: 'cover',
    bakeInsetShadow: true,
    label: 'Sponsor Logo',
  },
  // big3-signature-events per-event image (P-037.24 D-132; composite
  // reworked at P-037.26 D-134). Same 190 × 100 contract as event-
  // card-group: source images crop to 1.9:1. The composite pipeline
  // (post-D-134) draws this into a 190 × 150 Canvas at (0, 50) — these
  // dimensions are the SOURCE registry target, not the composite
  // output. Post-Phase-Close §item G — `mode: 'cover'` (was fit-inside;
  // same pixelation issue as the logos surfaced for non-1.9:1 sources
  // — the composite Canvas's cover-crop would upscale a fit-inside
  // result with shorter axis < target). NO `bakeInsetShadow` here —
  // the composite pipeline's internal `applyInsetShadow` call on the
  // event-image region of the final composite handles the shadow;
  // setting the flag would double-apply.
  'big3-signature-events.big3Events.imageUrl': {
    width: 190,
    height: 100,
    mode: 'cover',
    label: 'Big 3 Event Image',
  },
  // big3-signature-events per-event Big 3 logo (P-037.24 D-132).
  // 100 × 100 — kept at D-134 per Banks's pick from the P-037.26
  // kickoff (canvas grew to 190 × 150 to accommodate the overhang;
  // logo size unchanged). The composite pipeline (post-D-134) draws
  // the logo at (45, 0) with a circular clip-mask so the vertical
  // center sits on the navy/image boundary line (y = 50).
  // P-037.29 post-Phase-Close §item G — migrated from short-lived
  // `'outside'` mode (§item C) to `'cover'`. Cover-crop pre-crops the
  // source to 1:1 at upload; the composite's `coverSourceRect(200,
  // 200, 1)` becomes a no-op (already 1:1). Same final composite,
  // ~50% smaller upload bytes for tall/wide source logos.
  'big3-signature-events.big3Events.big3LogoUrl': {
    width: 100,
    height: 100,
    mode: 'cover',
    label: 'Big 3 Logo (small)',
  },
  // big3-signature-events signature-event image (P-037.24 D-132). Same
  // 190 × 100 as event-card-group; signatureEvents render via the
  // existing `eventCard()` Component (no fork), so dimensions match.
  // Post-Phase-Close §item G — `mode: 'cover'` + `bakeInsetShadow:
  // true` for parity with event-card-group (same Component, same IMG
  // behavior, same direct-render path).
  'big3-signature-events.signatureEvents.imageUrl': {
    width: 190,
    height: 100,
    mode: 'cover',
    bakeInsetShadow: true,
    label: 'Signature Event Image',
  },
  // images Block per-item image (P-037.28 D-136). The columns=1 path
  // renders at 620 × 326 (190 × 3 + 25 × 2 width; 1.9:1 aspect preserved
  // at the larger size); columns=3 renders at 190 × 100. One @1x bound
  // (620 × 326) works for BOTH column modes — columns=3 just renders the
  // upload smaller. Avoids per-Block-instance dimension dispatch
  // (registry is set at upload time, before staff picks columns). The
  // engineering trade favors quality (a columns=3 image scaled down
  // from 620 px still looks crisp) over bytes. Post-Phase-Close §item
  // G — `mode: 'cover'` (same aspect-stretch issue as event-card-group)
  // + `bakeInsetShadow: true` for parity. Trade-off note: shadow ring
  // baked at 620 × 326 source renders thicker on the columns=3 path
  // (browser downscales source 3.26× → shadow softens proportionally
  // but stays visible). Acceptable for V1.
  'images.images.imageUrl': {
    width: 620,
    height: 326,
    mode: 'cover',
    bakeInsetShadow: true,
    label: 'Photo',
  },
  // big-3-hero-image event image (P-037.28 D-136; reworked at P-037.29
  // D-137 §item 6 — 525 × 160 → 620 × 160). The event image lands at
  // (0, 95) inside the new 620 × 255 composite canvas, full canvas
  // width with a 10 px navy bar directly above. Aspect ratio
  // ~3.875:1; staff uploads cover-crop to that aspect at composite
  // time. See src/lib/image-composite.ts for the layout. Post-Phase-
  // Close §item G — `mode: 'cover'` (was fit-inside; same pixelation
  // issue as the Big-3 event image — composite Canvas's cover-crop
  // would upscale). NO `bakeInsetShadow` (composite pipeline's
  // internal `applyInsetShadow` call handles the shadow on the
  // composite PNG).
  'big-3-hero-image.imageUrl': {
    width: 620,
    height: 160,
    mode: 'cover',
    label: 'Big 3 Hero Image',
  },
  // big-3-hero-image logo (P-037.28 D-136). 190 × 190 — horizontally
  // centered at (215, 0) inside the 620 × 255 composite canvas;
  // circular-clipped at center (310, 95) radius 95. Vertical center
  // sits on the navy/image boundary line.
  // P-037.29 post-Phase-Close §item G — migrated from short-lived
  // `'outside'` mode (§item C) to `'cover'`. Cover-crop pre-crops the
  // source to 1:1 at upload; the composite's `coverSourceRect(380,
  // 380, 1)` becomes a no-op (already 1:1). Same final composite,
  // smaller upload bytes for non-square source logos.
  'big-3-hero-image.bigHeroLogoUrl': {
    width: 190,
    height: 190,
    mode: 'cover',
    label: 'Big 3 Logo (large)',
  },
})

// Catch-all when the key is unmapped — keeps the resize step active for
// future Block image fields that haven't been pinned yet.
const DEFAULT_TARGET: ImageTarget = Object.freeze({ width: 600, height: 600 })

/**
 * Look up the @1x rendered dimensions for an image field. Pass the
 * full dotted path `${blockId}.${fieldPath}` (e.g.
 * `author.authorPhotoUrl`, `event-card-group.events.0.imageUrl`).
 * Array indices are stripped so per-element fields share their array
 * parent's target.
 *
 * Returns `DEFAULT_TARGET` for any unmapped key — never undefined, so
 * the resize step always has dimensions to work with.
 */
export function getImageTarget(key: string | undefined): ImageTarget {
  if (key === undefined) return DEFAULT_TARGET
  const stripped = key.replace(/\.\d+(?=\.|$)/g, '')
  return IMAGE_TARGETS[stripped] ?? DEFAULT_TARGET
}

/** Returns the upload (@2x retina) dimensions for the given @1x target.
 *  Preserves `mode` + `bakeInsetShadow` + `label` so the consumer's fit
 *  semantics + shadow choice + UX label are honored at the @2x level
 *  (P-037.29 post-Phase-Close §item G; P-037.30 seventh post-Phase-Close
 *  §C adds the label propagation). */
export function toRetinaTarget(target: ImageTarget): ImageTarget {
  return {
    width: target.width * RETINA_SCALE,
    height: target.height * RETINA_SCALE,
    ...(target.mode !== undefined ? { mode: target.mode } : {}),
    ...(target.bakeInsetShadow !== undefined ? { bakeInsetShadow: target.bakeInsetShadow } : {}),
    ...(target.label !== undefined ? { label: target.label } : {}),
  }
}
